// Shared TanStack Query hooks. Components consume these — they never call the
// raw api module directly so caching/invalidation rules live in one place.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
export const qk = {
    health: ['health'],
    cameras: ['cameras'],
    streamHealth: ['stream-health'],
    cameraPlaybackUrl: (cameraId) => ['camera-playback-url', cameraId ?? ''],
    personas: ['personas'],
    alerts: (cameraId) => ['alerts', cameraId ?? 'all'],
    uploadedStructured: (cameraId) => ['uploaded-structured', cameraId ?? ''],
    uploadedStructuredConfig: (cameraId) => ['uploaded-structured-config', cameraId ?? ''],
    job: (jobId) => ['job', jobId ?? ''],
    redactJob: (jobId) => ['redact-job', jobId ?? ''],
    redactJobs: ['redact-jobs'],
    redactInputs: ['redact-inputs'],
    redactOutputs: ['redact-outputs'],
    redactRun: (jobId) => ['redact-run', jobId ?? ''],
    settings: ['admin-settings'],
};
function asList(v, key) {
    if (Array.isArray(v))
        return v;
    if (!v)
        return [];
    const obj = v;
    // Live API uses `items` for all list-shaped responses. Legacy code paths used
    // a topic-specific key (e.g. `cameras`, `personas`) — try both.
    for (const k of ['items', key]) {
        if (Array.isArray(obj[k]))
            return obj[k];
    }
    return [];
}
export function useHealth() {
    return useQuery({
        queryKey: qk.health,
        queryFn: () => api.health(),
        refetchInterval: 15_000,
    });
}
export function useCameras() {
    return useQuery({
        queryKey: qk.cameras,
        queryFn: async () => asList(await api.listCameras(), 'cameras'),
    });
}
export function useStreamHealth() {
    return useQuery({
        queryKey: qk.streamHealth,
        queryFn: async () => asList(await api.listCameraStreamHealth(), 'items'),
        refetchInterval: 10_000,
    });
}
export function useAddCamera() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => api.addCamera(body),
        onSuccess: () => qc.invalidateQueries({ queryKey: qk.cameras }),
    });
}
export function useStartCamera() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api.startCamera(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: qk.cameras }),
    });
}
export function useStopCamera() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api.stopCamera(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: qk.cameras }),
    });
}
export function useDeleteCamera() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api.deleteCamera(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: qk.cameras }),
    });
}
export function useCameraPlaybackUrl(cameraId) {
    return useQuery({
        queryKey: qk.cameraPlaybackUrl(cameraId ?? undefined),
        queryFn: () => api.getCameraPlaybackUrl(cameraId),
        enabled: !!cameraId,
        staleTime: 60_000,
    });
}
export function usePersonas() {
    return useQuery({
        queryKey: qk.personas,
        queryFn: async () => asList(await api.listPersonas(), 'personas'),
    });
}
export function useUpsertPersona() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => api.upsertPersona(body),
        onSuccess: () => qc.invalidateQueries({ queryKey: qk.personas }),
    });
}
export function useSummarize() {
    return useMutation({ mutationFn: (body) => api.summarize(body) });
}
export function useUploadedStructuredAnalytics(cameraId) {
    return useQuery({
        queryKey: qk.uploadedStructured(cameraId ?? undefined),
        queryFn: () => api.getUploadedStructuredAnalytics(cameraId),
        enabled: !!cameraId,
        refetchInterval: 5_000,
    });
}
export function useUploadedStructuredConfig(cameraId) {
    return useQuery({
        queryKey: qk.uploadedStructuredConfig(cameraId ?? undefined),
        queryFn: () => api.getUploadedStructuredConfig(cameraId),
        enabled: !!cameraId,
    });
}
export function useUpdateUploadedStructuredConfig() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ cameraId, config }) => api.updateUploadedStructuredConfig(cameraId, config),
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: qk.uploadedStructured(vars.cameraId) });
            qc.invalidateQueries({ queryKey: qk.uploadedStructuredConfig(vars.cameraId) });
        },
    });
}
export function useRunUploadedStructuredAnalytics() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ cameraId, triggerVss = true, notes }) => api.runUploadedStructuredAnalytics(cameraId, { trigger_vss: triggerVss, notes }),
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: qk.uploadedStructured(vars.cameraId) });
            qc.invalidateQueries({ queryKey: qk.alerts(vars.cameraId) });
        },
    });
}
/**
 * Polls a single LVS job until it reaches a terminal state. Pass `null`
 * to disable polling. Refetches every 3s while queued/running.
 */
export function useJob(jobId) {
    return useQuery({
        queryKey: qk.job(jobId ?? undefined),
        queryFn: () => api.getJob(jobId),
        enabled: !!jobId,
        refetchInterval: (q) => {
            const data = q.state.data;
            if (!data)
                return 3_000;
            if (data.status === 'queued' || data.status === 'running')
                return 3_000;
            return false;
        },
    });
}
export function useCancelJob() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (jobId) => api.cancelJob(jobId),
        onSuccess: (_d, jobId) => qc.invalidateQueries({ queryKey: qk.job(jobId) }),
    });
}
/** Kick off a redaction job for an existing camera/uploaded video. */
export function useSubmitRedaction() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ cameraId, options }) => api.submitRedaction(cameraId, options),
        onSuccess: () => qc.invalidateQueries({ queryKey: qk.redactJobs }),
    });
}
/** List all redaction jobs (queued/running/done/failed). Polls every 3s. */
export function useRedactJobs(status) {
    return useQuery({
        queryKey: [...qk.redactJobs, status ?? 'all'],
        queryFn: async () => asList(await api.listRedactJobs(status), 'items'),
        refetchInterval: 3_000,
    });
}
/** Poll a single redaction job. On terminal status, invalidate cameras list
 * so the newly-created redacted sibling appears in pickers. */
export function useRedactJob(jobId) {
    const qc = useQueryClient();
    return useQuery({
        queryKey: qk.redactJob(jobId ?? undefined),
        queryFn: async () => {
            const j = await api.getRedactJob(jobId);
            if (j.status === 'done' || j.status === 'failed') {
                qc.invalidateQueries({ queryKey: qk.cameras });
                qc.invalidateQueries({ queryKey: qk.redactJobs });
            }
            return j;
        },
        enabled: !!jobId,
        refetchInterval: (q) => {
            const data = q.state.data;
            if (!data)
                return 3_000;
            if (data.status === 'queued' || data.status === 'running')
                return 3_000;
            return false;
        },
    });
}
export function useCancelRedactJob() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (jobId) => api.cancelRedactJob(jobId),
        onSuccess: (_d, jobId) => {
            qc.invalidateQueries({ queryKey: qk.redactJob(jobId) });
            qc.invalidateQueries({ queryKey: qk.redactJobs });
        },
    });
}
export function useChat() {
    return useMutation({ mutationFn: (body) => api.chat(body) });
}
/** Brev-parity standalone redaction flow */
export function useRedactInputs() {
    return useQuery({
        queryKey: qk.redactInputs,
        queryFn: () => api.listRedactInputs(),
        refetchInterval: 10_000,
    });
}
export function useRedactOutputs() {
    return useQuery({
        queryKey: qk.redactOutputs,
        queryFn: () => api.listRedactOutputs(),
        refetchInterval: 10_000,
    });
}
export function useUploadRedact() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (file) => api.uploadRedact(file),
        onSuccess: () => qc.invalidateQueries({ queryKey: qk.redactInputs }),
    });
}
export function useSubmitRedactRun() {
    return useMutation({
        mutationFn: (body) => api.submitRedactRun(body),
    });
}
/** Poll a /redact/run/{id} job for status + streaming logs. */
export function useRedactRun(jobId) {
    const qc = useQueryClient();
    return useQuery({
        queryKey: qk.redactRun(jobId ?? undefined),
        queryFn: async () => {
            const j = await api.getRedactRun(jobId);
            if (j.status === 'completed' || j.status === 'failed') {
                qc.invalidateQueries({ queryKey: qk.redactOutputs });
            }
            return j;
        },
        enabled: !!jobId,
        refetchInterval: (q) => {
            const data = q.state.data;
            if (!data)
                return 1_500;
            if (data.status === 'completed' || data.status === 'failed')
                return false;
            return 1_500;
        },
    });
}
export function useSearch() {
    return useMutation({ mutationFn: (body) => api.search(body) });
}
export function useLiveAlerts(cameraId) {
    return useQuery({
        queryKey: qk.alerts(cameraId),
        queryFn: async () => asList(await api.liveAlerts(cameraId), 'alerts'),
        refetchInterval: 10_000,
    });
}
export function useAdminSettings() {
    return useQuery({
        queryKey: qk.settings,
        queryFn: () => api.getSettings(),
        staleTime: 30_000,
    });
}
export function useUpdateAdminSettings() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch) => api.updateSettings(patch),
        onSuccess: () => qc.invalidateQueries({ queryKey: qk.settings }),
    });
}
